import ElementText from 'src/editor/text/ElementText'
import {
  CardFragment,
  ColorFragment,
  ElementFragment,
  ElementPictureFragment,
  ImageFragment,
  Maybe,
  PanelFragment,
} from 'src/graphql'
import { TextFragment } from 'src/editor/types'

export type Position = { x: number; y: number }

export type EditorImage = {
  image: Maybe<ImageFragment>
  filter: Maybe<{ name: string }>
  position: Position
  scale: number
  filterUrl?: string
}

const EditorImage = (image: ElementPictureFragment): EditorImage => ({
  image: image.image,
  filter: image.filter,
  position: image.position,
  scale: image.scale,
})

export type EditorElement = Omit<ElementFragment, 'image' | 'text'> & {
  image: Maybe<EditorImage>
  text: Maybe<TextFragment[]>
}

export const EditorElement = (element: ElementFragment): EditorElement => ({
  ...element,
  image: element.image ? EditorImage(element.image) : null,
  text: element.text ? ElementText(element.text) : null,
})

export type EditorPanel = {
  backgroundColor: Maybe<ColorFragment>
  name: string
  isLocked: boolean
  x: number
  y: number
  width: number
  height: number
  elements: EditorElement[]
  previewUrl?: string
}

export type EditorFullBleed = {
  name: string
  width: number
  height: number
  panels: EditorPanel[]
  backgroundElement: Maybe<EditorElement>
}

const simpleFullBleed = (panel: PanelFragment): EditorFullBleed => ({
  name: panel.name,
  width: panel.width,
  height: panel.height,
  panels: [
    {
      backgroundColor: panel.backgroundColor,
      name: panel.name,
      isLocked: panel.isLocked,
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      elements: panel.elements.map(EditorElement),
    },
  ],
  backgroundElement: null,
})

const insideFullBleed = (
  panels: PanelFragment[],
  fullBleedPanel: PanelFragment | undefined,
  isHorizontal: boolean,
): EditorFullBleed => {
  const panelWidth = panels[0].width
  const panelHeight = panels[0].height
  const width = isHorizontal ? panelWidth : panelWidth * panels.length
  const height = isHorizontal ? panelHeight * panels.length : panelHeight
  return {
    name: 'Inside',
    width,
    height,
    panels: panels.map((panel, index) => ({
      previewUrl: panel.previewUrl,
      backgroundColor: panel.backgroundColor,
      name: panel.name,
      isLocked: panel.isLocked,
      x: isHorizontal ? 0 : index / panels.length,
      y: isHorizontal ? index / panels.length : 0,
      width: isHorizontal ? 1 : 1 / panels.length,
      height: isHorizontal ? 1 / panels.length : 1,
      elements: panel.elements.map(EditorElement),
    })),
    backgroundElement: fullBleedPanel?.elements.length
      ? EditorElement(fullBleedPanel.elements[0])
      : {
          x: 0,
          y: 0,
          z: 0,
          rotation: 0,
          width: 1,
          height: 1,
          locked: false,
          image: {
            image: null,
            position: { x: 0.5, y: 0.5 },
            scale: 1,
            filter: null,
          },
          text: null,
        },
  }
}

export const factoryFullBleeds = (card: CardFragment) => {
  const panels = card.panels.filter(panel => !panel.isFullBleed)
  const insideFullBleedPanel = card.panels.find(
    panel => panel.isFullBleed && panel.location === 1,
  )
  switch (card.type) {
    case 'POSTCARD':
    case 'FLATCARD':
      return panels.map(simpleFullBleed)
    case 'TWO_PANEL':
    case 'TWO_PANEL_BIG':
      return [
        simpleFullBleed(panels[0]),
        insideFullBleed(
          panels.slice(1, 3),
          insideFullBleedPanel,
          card.isHorizontal,
        ),
        simpleFullBleed(panels[3]),
      ]
    case 'THREE_PANEL':
      return [
        simpleFullBleed(panels[0]),
        insideFullBleed(
          panels.slice(1, 4),
          insideFullBleedPanel,
          card.isHorizontal,
        ),
        simpleFullBleed(panels[4]),
        simpleFullBleed(panels[5]),
      ]
    default:
      return []
  }
}
